// EID-131: GeoZarr multiscale conventions for the `zarr` datasource type.
//
// Scope: convention parsing plus a level-addressable open interface. The LOD
// *policy* (what resolution to ask for, when to switch, hysteresis, per-tile
// traversal) is deliberately not here — a future scheduler computes a
// resolution hint and calls openScale({ resolution }).
//
// Two design decisions are load-bearing and should survive refactors:
//
//  1. The access currency is RESOLUTION IN GROUND UNITS PER PIXEL, never a
//     zoom level. EIDOS renders map, globe, first-person and VR views; only
//     the map view has a "zoom", and only a mercator map has WebMercatorQuad's
//     zoom→resolution table. A TileMatrixSet association is optional metadata
//     we surface, never a coordinate system we assume.
//
//  2. Every metadata read goes through the ATTRIBUTE_ALIASES table below. The
//     GeoZarr convention is still moving through OGC review and its published
//     spec document lags the convention repos, so stores in the wild carry
//     several spellings of the same idea (the current draft's
//     `tile_matrix_set` / `resampling_method`, OME-Zarr's
//     `datasets[].coordinateTransformations`, STAC's `proj:` namespace,
//     ad-hoc `cellSize`). An attribute rename must cost one table entry, not
//     a re-ingest or a scattered edit.

/** A single pyramid level, as addressed by openScale. */
export interface ScaleLevel {
  /**
   * Numeric level identifier, as named by the child group. In the GeoZarr /
   * web-tiling convention level 0 is the COARSEST (one 256x256 tile covering
   * the CRS extent) and each increment halves the cell size.
   */
  level: number;
  /** Group path relative to the store root, e.g. `'2'`. */
  path: string;
  /**
   * Ground units per pixel as [x, y], in the units of this level's CRS —
   * degrees for a geographic CRS, metres for a projected one. Absent when the
   * store carries no resolution metadata (bare numbered-group stores).
   */
  cellSize?: [number, number];
  /** CRS identifier, normalized to `EPSG:<code>` when given as an EPSG code. */
  crs?: string;
  /** TileMatrixSet identifier when the store declares one. Never assumed. */
  tileMatrixSet?: string;
  /**
   * Downsampling operator that produced this level (mean/max/subsample/...).
   * Surfaced so legends can disclose that a coarse level is an aggregate
   * rather than a subsample of the source.
   */
  aggregation?: string;
}

/** The multiscale surface attached to an opened zarr datasource. */
export interface MultiscaleAccess<T = unknown> {
  /** Levels ordered fine → coarse (see sortLevels for the ordering rule). */
  scales: ScaleLevel[];
  /**
   * Open one level as a Dataset — the same handle shape worldlayers already
   * consume. A number selects by level identifier; `{ resolution }` selects by
   * ground units per pixel (see selectScale).
   */
  openScale: (hint: number | { resolution: number }) => Promise<T>;
}

/**
 * Attribute-name churn absorber. Each logical field lists the spellings we
 * accept, most-preferred first; `pick` returns the first present value. Adding
 * a spelling is a one-line change here.
 */
const ATTRIBUTE_ALIASES = {
  /** Container for the multiscale declaration on the root group. */
  multiscales: ['multiscales', 'multiscale', 'geozarr:multiscales'],
  /** Per-level ground sample distance, written [x, y] or as a scalar. */
  cellSize: [
    'cellSize',
    'cell_size',
    'resolution',
    'spatial_resolution',
    'geozarr:cell_size',
  ],
  /** CRS identifier. `proj:` names come from the STAC projection extension. */
  crs: ['proj:code', 'proj:epsg', 'proj:wkt2', 'crs', 'spatial_ref'],
  /** TileMatrixSet association — identifier, URI, or inline TMS object. */
  tileMatrixSet: [
    'tile_matrix_set',
    'tileMatrixSet',
    'tile_matrix_set_id',
    'tileMatrixSetId',
  ],
  /** Downsampling operator. `resampling_method` is the current draft's name. */
  aggregation: [
    'resampling_method',
    'aggregation',
    'aggregation_method',
    'downsampling_method',
    'downsampling',
  ],
  /** Enumeration of the levels inside a multiscales declaration. */
  levels: ['datasets', 'levels', 'children'],
} as const;

type Attrs = Record<string, unknown>;
type AttrField = keyof typeof ATTRIBUTE_ALIASES;

/** First present value for a logical field. The single metadata read path. */
const pick = (attrs: Attrs | null | undefined, field: AttrField): unknown => {
  if (!attrs) return undefined;
  for (const alias of ATTRIBUTE_ALIASES[field]) {
    const value = attrs[alias];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const isPositive = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0;

const asString = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw.length ? raw : undefined;

/** `4326` → `'EPSG:4326'`; other strings pass through; anything else drops. */
const normalizeCrs = (raw: unknown): string | undefined => {
  if (isPositive(raw)) return `EPSG:${raw}`;
  const text = asString(raw);
  if (!text) return undefined;
  // A bare numeric string is an EPSG code written as text.
  return /^\d+$/.test(text) ? `EPSG:${text}` : text;
};

/**
 * An explicit resolution declaration: a scalar (square cells) or an [x, y]
 * pair. Deliberately separate from a scale VECTOR (see scaleVectorCellSize) —
 * the two conventions order their axes oppositely.
 */
const declaredCellSize = (raw: unknown): [number, number] | undefined => {
  if (isPositive(raw)) return [raw, raw];
  if (
    Array.isArray(raw) &&
    raw.length === 2 &&
    isPositive(raw[0]) &&
    isPositive(raw[1])
  ) {
    return [raw[0], raw[1]];
  }
  return undefined;
};

/**
 * An OME-Zarr `coordinateTransformations` scale vector, whose axes follow the
 * array's. Geospatial arrays are (..., y, x), so the last two entries are read
 * as [y, x] and returned as [x, y].
 */
const scaleVectorCellSize = (raw: unknown): [number, number] | undefined => {
  if (!Array.isArray(raw) || raw.length < 2) return undefined;
  const y = raw[raw.length - 2];
  const x = raw[raw.length - 1];
  return isPositive(x) && isPositive(y) ? [x, y] : undefined;
};

/** cellSize from the direct aliases, else from a coordinateTransformations scale. */
const cellSizeFrom = (
  attrs: Attrs | null | undefined,
): [number, number] | undefined => {
  const direct = declaredCellSize(pick(attrs, 'cellSize'));
  if (direct) return direct;
  const transforms = attrs?.['coordinateTransformations'];
  if (!Array.isArray(transforms)) return undefined;
  for (const t of transforms) {
    if (t && typeof t === 'object' && (t as Attrs).type === 'scale') {
      const scale = scaleVectorCellSize((t as Attrs).scale);
      if (scale) return scale;
    }
  }
  return undefined;
};

/** TileMatrixSet identifier from an id, a URI, or an inline TMS object. */
const tileMatrixSetId = (raw: unknown): string | undefined => {
  const text = asString(raw);
  if (text) {
    const last = text.includes('/')
      ? text.split('/').filter(Boolean).pop()
      : text;
    return last?.replace(/\.json$/i, '') || undefined;
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return asString((raw as Attrs).id ?? (raw as Attrs).identifier);
  }
  return undefined;
};

/**
 * Per-level cellSize declared by an inline OGC TileMatrixSet — TMS 2.0 gives
 * every tileMatrix an `id` and a `cellSize`. Returns a level-id → cellSize
 * map; empty when the association is by identifier/URI rather than inline.
 *
 * `scaleDenominator` is deliberately NOT converted to a cellSize: the OGC
 * formula needs the CRS's metresPerUnit, and guessing that is exactly the
 * mercator assumption this module refuses to make.
 */
const tmsCellSizes = (raw: unknown): Record<string, [number, number]> => {
  const out: Record<string, [number, number]> = {};
  if (!raw || typeof raw !== 'object') return out;
  const matrices =
    (raw as Attrs).tileMatrices ?? (raw as Attrs).tileMatrix ?? null;
  if (!Array.isArray(matrices)) return out;
  for (const m of matrices) {
    if (!m || typeof m !== 'object') continue;
    const id = (m as Attrs).id ?? (m as Attrs).identifier;
    const size = declaredCellSize((m as Attrs).cellSize);
    if (size && (typeof id === 'string' || typeof id === 'number')) {
      out[String(id)] = size;
    }
  }
  return out;
};

/** The multiscales declaration, tolerating the object and array-wrapped forms. */
const multiscalesDeclaration = (
  rootAttrs: Attrs | null | undefined,
): Attrs | null => {
  const raw = pick(rootAttrs, 'multiscales');
  if (Array.isArray(raw)) {
    const first = raw[0];
    return first && typeof first === 'object' ? (first as Attrs) : null;
  }
  return raw && typeof raw === 'object' ? (raw as Attrs) : null;
};

/**
 * The coarser of a level's two axes. Comparing on the max means a selected
 * level is at least as fine as the hint on BOTH axes, which is the safe
 * reading of "cellSize ≤ hint" for anisotropic grids.
 */
const maxCell = (level: ScaleLevel): number =>
  level.cellSize ? Math.max(level.cellSize[0], level.cellSize[1]) : Infinity;

/**
 * Level ordering: fine → coarse.
 *
 * When every level has a known cellSize, order by cell size ascending — the
 * only view-agnostic ordering that means anything. Otherwise fall back to
 * level identifier DESCENDING, which is the GeoZarr / web-tiling convention
 * (level 0 coarsest, each increment finer). Ties break on level identifier
 * ascending so the result is stable across reopens.
 */
const sortLevels = (levels: ScaleLevel[]): ScaleLevel[] => {
  const allSized = levels.every((l) => l.cellSize);
  return [...levels].sort((a, b) => {
    if (!allSized) return b.level - a.level;
    const d = maxCell(a) - maxCell(b);
    return d !== 0 ? d : a.level - b.level;
  });
};

/**
 * Level selection for a resolution hint, in ground units per pixel expressed
 * in the store's own CRS units. No unit conversion is attempted — a hint in
 * degrees against a metric store is the caller's bug.
 *
 * Rule: the COARSEST level whose cellSize ≤ hint — the cheapest level that
 * still resolves what the hint asked for.
 *
 * Documented edge cases:
 *  - Ties (levels of equal cellSize): lowest level identifier wins, so the
 *    choice is stable across reopens.
 *  - Hint finer than every level: the finest level (best available).
 *  - Hint not finite/positive, or no level carries a cellSize: the finest
 *    level, i.e. the unchanged full-resolution behaviour.
 */
export const selectScale = (
  scales: ScaleLevel[],
  resolution: number,
): ScaleLevel | null => {
  if (!scales.length) return null;
  const finest = scales[0];
  if (!isPositive(resolution)) return finest;
  let best: ScaleLevel | null = null;
  for (const level of scales) {
    if (!level.cellSize || maxCell(level) > resolution) continue;
    if (
      !best ||
      maxCell(level) > maxCell(best) ||
      (maxCell(level) === maxCell(best) && level.level < best.level)
    ) {
      best = level;
    }
  }
  return best ?? finest;
};

/**
 * Numeric child-group names found among a set of store paths — the graceful
 * degradation path for the current ad-hoc layout (numbered child groups, no
 * multiscale metadata).
 *
 * A numbered segment only counts as a level when something lives UNDER it
 * (`'2/sst'`, not `'2'`). Otherwise a flat store whose variables happen to be
 * named '0', '1', … — band indices, say — would be mistaken for a pyramid, and
 * the levels it reported could not be opened: those names are arrays, and
 * addressing one as a group fails. Every real level group holds at least one
 * array, so nothing legitimate is lost.
 */
const numberedChildGroups = (paths: Iterable<string>): number[] => {
  const levels = new Set<number>();
  for (const path of paths) {
    const match = /^\/?(\d+)\//.exec(path);
    if (match) levels.add(Number(match[1]));
  }
  return [...levels].sort((a, b) => a - b);
};

/** First defined candidate as a single-key object, or nothing at all. */
const pickInto = <K extends string>(
  key: K,
  candidates: Array<string | undefined>,
): Partial<Record<K, string>> => {
  const value = candidates.find((c) => c !== undefined);
  return value === undefined ? {} : ({ [key]: value } as Record<K, string>);
};

/**
 * Build level descriptors from root attributes, per-level attributes and the
 * store's group/variable paths. Returns null when nothing indicates a
 * multiscale store — which is what keeps every non-multiscale datasource on
 * exactly its current code path.
 */
export const buildScaleLevels = ({
  rootAttributes,
  levelAttributes = {},
  paths = [],
}: {
  rootAttributes?: Attrs | null;
  /** Group path (e.g. `'2'`) → that group's attributes. */
  levelAttributes?: Record<string, Attrs>;
  /** Group or variable paths seen in the store. */
  paths?: Iterable<string>;
}): ScaleLevel[] | null => {
  const declaration = multiscalesDeclaration(rootAttributes);
  const tmsRaw =
    pick(declaration, 'tileMatrixSet') ?? pick(rootAttributes, 'tileMatrixSet');
  const inlineCellSizes = tmsCellSizes(tmsRaw);
  const defaults = {
    crs: normalizeCrs(pick(declaration, 'crs') ?? pick(rootAttributes, 'crs')),
    tileMatrixSet: tileMatrixSetId(tmsRaw),
    aggregation: asString(
      pick(declaration, 'aggregation') ?? pick(rootAttributes, 'aggregation'),
    ),
  };

  // Level enumeration, in priority order: an explicit list inside the
  // declaration, then an inline TileMatrixSet's matrices, then the store's
  // numbered child groups.
  const declared = pick(declaration, 'levels');
  let entries: Array<{ path: string; attrs: Attrs }>;
  if (Array.isArray(declared)) {
    entries = declared
      .map((entry) => {
        const attrs = (entry ?? {}) as Attrs;
        const path = String(attrs.path ?? attrs.id ?? attrs.name ?? '').replace(
          /^\//,
          '',
        );
        return { path, attrs };
      })
      .filter((e) => e.path.length > 0);
  } else if (Object.keys(inlineCellSizes).length) {
    entries = Object.keys(inlineCellSizes).map((path) => ({ path, attrs: {} }));
  } else {
    entries = numberedChildGroups(paths).map((level) => ({
      path: String(level),
      attrs: {},
    }));
  }
  if (!entries.length) return null;
  // One numbered group and no declaration is a plain store that happens to
  // contain a group, not a pyramid: wrapping it in a single-level API would
  // add nothing the base open does not already provide.
  if (entries.length < 2 && !declaration) return null;

  const levels: ScaleLevel[] = entries.map(({ path, attrs }, index) => {
    // Level-specific metadata wins over the declaration entry's, which wins
    // over the store-wide defaults.
    const groupAttrs = levelAttributes[path] ?? levelAttributes[`/${path}`];
    const cellSize =
      cellSizeFrom(groupAttrs) ?? cellSizeFrom(attrs) ?? inlineCellSizes[path];
    const level = Number(path.split('/').pop());
    return {
      // A non-numeric group name still gets a stable identifier: its position.
      level: Number.isFinite(level) ? level : index,
      path,
      ...(cellSize ? { cellSize } : {}),
      ...pickInto('crs', [
        normalizeCrs(pick(groupAttrs, 'crs')),
        normalizeCrs(pick(attrs, 'crs')),
        defaults.crs,
      ]),
      ...pickInto('tileMatrixSet', [
        tileMatrixSetId(pick(groupAttrs, 'tileMatrixSet')),
        tileMatrixSetId(pick(attrs, 'tileMatrixSet')),
        defaults.tileMatrixSet,
      ]),
      ...pickInto('aggregation', [
        asString(pick(groupAttrs, 'aggregation')),
        asString(pick(attrs, 'aggregation')),
        defaults.aggregation,
      ]),
    };
  });
  return sortLevels(levels);
};

/**
 * Consolidated zarr metadata (`.zmetadata`) — the one document carrying both
 * the root attributes and every child group's. A level open reports the LEVEL
 * group's attributes, so a pyramid's own metadata is only reachable from here.
 *
 * v2 consolidated metadata only, which is all the zarr datasource path
 * supports: Dataset.zarr refuses stores without `.zmetadata` outright.
 */
export interface ConsolidatedMetadata {
  metadata?: Record<string, unknown>;
}

/**
 * Fetch and parse a store's consolidated metadata. Returns null for any
 * failure (missing `.zmetadata`, non-JSON body, network error): a store we
 * cannot introspect is simply not multiscale, and must keep working exactly as
 * it does today.
 */
export const fetchConsolidatedMetadata = async (
  url: string,
  headers: Record<string, string> = {},
  fetchImpl: typeof fetch = fetch,
): Promise<ConsolidatedMetadata | null> => {
  const root = url.endsWith('/') ? url.slice(0, -1) : url;
  try {
    const response = await fetchImpl(`${root}/.zmetadata`, { headers });
    if (!response.ok) return null;
    const body = await response.json();
    return body && typeof body === 'object'
      ? (body as ConsolidatedMetadata)
      : null;
  } catch {
    return null;
  }
};

/** Split consolidated metadata into root attrs, per-group attrs and paths. */
export const readConsolidatedMetadata = (
  consolidated: ConsolidatedMetadata | null | undefined,
) => {
  const rootAttributes: Attrs = {};
  const levelAttributes: Record<string, Attrs> = {};
  const paths: string[] = [];
  const entries = consolidated?.metadata;
  if (!entries || typeof entries !== 'object') {
    return { rootAttributes, levelAttributes, paths };
  }
  for (const [key, value] of Object.entries(entries)) {
    const attrs = (value ?? {}) as Attrs;
    if (key === '.zattrs') {
      Object.assign(rootAttributes, attrs);
      continue;
    }
    const group = key.replace(/\/\.z(attrs|group|array)$/, '');
    // Not a metadata key under a group (e.g. the root's own .zgroup).
    if (group === key) continue;
    paths.push(group);
    if (key.endsWith('/.zattrs')) levelAttributes[group] = attrs;
  }
  return { rootAttributes, levelAttributes, paths };
};

/**
 * Detect a pyramid from an already-open root Dataset — the free path, costing
 * no extra request. Evidence is a multiscales declaration on the root group or
 * numbered child groups among the variable names (the datamesh client reports
 * a nested array as `'<group>/<var>'`).
 *
 * Returns null for a flat store, which is the overwhelmingly common case.
 */
export const detectMultiscaleFromDataset = (dataset: {
  attributes?: Attrs;
  variables?: Record<string, unknown>;
}): ScaleLevel[] | null =>
  buildScaleLevels({
    rootAttributes: dataset?.attributes ?? null,
    paths: Object.keys(dataset?.variables ?? {}),
  });

/**
 * True when root metadata claims a multiscale layout, regardless of whether
 * per-level detail is reachable. Decides whether the extra `.zmetadata` read
 * is worth making.
 */
export const declaresMultiscale = (rootAttributes?: Attrs | null): boolean =>
  multiscalesDeclaration(rootAttributes) !== null;

/**
 * Wrap level descriptors in the openScale interface. `open` receives a group
 * path relative to the store root and returns the same Dataset handle shape a
 * non-multiscale open produces, so consumers need no special case.
 */
export const createMultiscaleAccess = <T>({
  scales,
  open,
}: {
  scales: ScaleLevel[];
  open: (path: string) => Promise<T>;
}): MultiscaleAccess<T> => ({
  scales,
  openScale: (hint) => {
    const target =
      typeof hint === 'number'
        ? (scales.find((s) => s.level === hint) ?? null)
        : selectScale(scales, hint?.resolution);
    if (!target) {
      const available = scales.map((s) => s.level).join(', ');
      return Promise.reject(
        new Error(
          `No multiscale level ${JSON.stringify(hint)} (available: ${
            available || 'none'
          })`,
        ),
      );
    }
    return open(target.path);
  },
});

/**
 * The zarr datasource open flow, multiscale-aware. Kept here rather than
 * inline in the data provider so the back-compat behaviour below is directly
 * testable; the provider supplies the datamesh-backed callbacks.
 *
 *  - `open(undefined)` must be the caller's UNCHANGED base open, and
 *    `open(path)` the same open scoped to a level group.
 *  - `loadMetadata` reads the store's consolidated metadata. Only called when
 *    the store shows multiscale evidence, so a flat store costs no extra
 *    request; returning null just means "not a describable pyramid".
 *
 * Ordering is the back-compat guarantee: the base open happens first and its
 * result is what the caller gets, so a non-multiscale store — and a spec whose
 * `group` already pins one level — behaves exactly as before.
 */
export const openWithScales = async <
  T extends { attributes?: Attrs; variables?: Record<string, unknown> },
>({
  open,
  loadMetadata,
  onFallback,
}: {
  open: (path?: string) => Promise<T>;
  loadMetadata: () => Promise<ConsolidatedMetadata | null>;
  /** Notified when the base open failed and a level was opened instead. */
  onFallback?: (level: ScaleLevel, error: unknown) => void;
}): Promise<{ dataset: T; multiscale: MultiscaleAccess<T> | null }> => {
  const scalesFromMetadata = async () => {
    const consolidated = await loadMetadata();
    return consolidated
      ? buildScaleLevels(readConsolidatedMetadata(consolidated))
      : null;
  };

  let dataset: T | null = null;
  let openError: unknown = null;
  try {
    dataset = await open();
  } catch (e) {
    openError = e;
  }

  if (dataset) {
    // Free path: the root attributes and '<level>/<var>' variable names the
    // open we just did already returned.
    const detected = detectMultiscaleFromDataset(dataset);
    if (!detected && !declaresMultiscale(dataset.attributes)) {
      return { dataset, multiscale: null };
    }
    // Per-level cellSize lives in each level group's own attributes, which a
    // root open does not report — so an evidenced store pays for the read.
    const scales = (await scalesFromMetadata()) ?? detected;
    return {
      dataset,
      multiscale: scales ? createMultiscaleAccess({ scales, open }) : null,
    };
  }

  // The base open failed. A pyramid root is the expected reason: levels of a
  // real pyramid share dimension names at different sizes, so opening the root
  // walks every level's arrays at once and the datamesh client rejects it with
  // "Inconsistent dimension size". Such a store currently yields an unhandled
  // rejection and no data; fall back to the coarsest level — the cheapest one
  // that renders — so it is usable, and let a future LOD scheduler refine.
  const scales = await scalesFromMetadata();
  // Nothing describable to fall back to, so surface the original failure —
  // ?? covers an opener that rejected with a falsy value or resolved nothing,
  // either of which would otherwise `throw null`.
  if (!scales?.length) {
    throw openError ?? new Error('Zarr open returned no dataset');
  }
  const coarsest = scales[scales.length - 1];
  onFallback?.(coarsest, openError);
  return {
    dataset: await open(coarsest.path),
    multiscale: createMultiscaleAccess({ scales, open }),
  };
};
